import { ticketSortOptions } from '@/components/TableAction/TicketFilterHeader';
import { ISearch } from '@/hooks/useSearchStore';
import { TicketFilters } from '@/stores';
import { OrderFilters } from '@/stores/useFilterOrderStore';
import { ISearchTicket, TicketSortByEnum, TicketSortKeyEnum } from '@/stores/useTicketingStore';
import { TransactionFilters } from '@/stores/useTransactionFilteringStore';
import { mapFilterToApiStatuses, NormalizedTicketStatus } from '@/utils/ticketStatus';

export const createPaginationSearchParams = (
  page: number,
  pageSize?: number,
  search?: ISearch,
): URLSearchParams => {
  const params = new URLSearchParams({
    page: page.toString() || '1',
    perpage: (pageSize || 25).toString(),
  });

  if (search?.searchBy && search?.search) {
    params.append(search.searchBy, search.search);
  }

  return params;
};

export const getFilterTransactionQuery = (TransactionFilters?: TransactionFilters) => {
  const params = new URLSearchParams();
  if (!TransactionFilters) return params;

  if (TransactionFilters.createdAtFilter?.[0]) {
    params.append(
      'filter_created_at_from',
      TransactionFilters.createdAtFilter[0].format('YYYY-MM-DD HH:mm:ss'),
    );
  }

  if (TransactionFilters.createdAtFilter?.[1]) {
    params.append(
      'filter_created_at_to',
      TransactionFilters.createdAtFilter[1].format('YYYY-MM-DD HH:mm:ss'),
    );
  }

  if (TransactionFilters.statusFilter) {
    TransactionFilters.statusFilter.forEach((orderType) => {
      params.append('filter_statuses[]', orderType);
    });
  }

  if (TransactionFilters.typeFilter) {
    params.append('filter_type', TransactionFilters.typeFilter);
  }

  return params;
};

export const getFilterTicketQuery = (
  ticketFilters?: TicketFilters,
  platformCode?: string,
  ticketIds?: string[],
  sortBy?: TicketSortKeyEnum,
  customId?: string,
) => {
  const params = new URLSearchParams();

  if (!ticketFilters && !platformCode && !ticketIds && !sortBy && !customId) return params;

  if (ticketFilters) {
    if (ticketFilters.createdAtFilter?.[0]) {
      params.append('from_date', ticketFilters.createdAtFilter[0].format('YYYY-MM-DD'));
    }

    if (ticketFilters.createdAtFilter?.[1]) {
      params.append('to_date', ticketFilters.createdAtFilter[1].format('YYYY-MM-DD'));
    }

    if (ticketFilters.statusFilter && ticketFilters.statusFilter.length > 0) {
      params.append('fields[0][title]', 'status');
      const apiStatuses = mapFilterToApiStatuses(
        ticketFilters.statusFilter as NormalizedTicketStatus[],
      );
      apiStatuses.forEach((status) => {
        params.append('fields[0][value][]', status);
      });
    }
  }
  if (platformCode) params.append('platform_code', platformCode);

  if (customId) params.append('custom_id', customId);

  if (sortBy) getSortByParams(params, sortBy);

  if (ticketIds && ticketIds.length > 0)
    ticketIds.forEach((ticketId) => {
      params.append(`ids[]`, ticketId);
    });

  return params;
};

export const getSortByParams = (params: URLSearchParams, sortKey?: TicketSortKeyEnum) => {
  if (sortKey) {
    const { sort_by, sort_order } = ticketSortOptions[sortKey];
    params.append('sort_by', sort_by);
    params.append('sort_order', sort_order);
  }
  return params;
};

export const getOrderParams = (orderFilters: OrderFilters) => {
  if (
    !orderFilters.createdAtFilter &&
    (!orderFilters.orderTypeFilter || orderFilters.orderTypeFilter.length === 0) &&
    (!orderFilters.carrierFilter || orderFilters.carrierFilter.length === 0) &&
    orderFilters.printedFilter === undefined
  )
    return undefined;
  const params = new URLSearchParams();

  if (orderFilters.createdAtFilter) {
    params.append(
      'filter_created_at_from',
      orderFilters.createdAtFilter[0]?.format('YYYY-MM-DD HH:mm:ss'),
    );
    params.append(
      'filter_created_at_to',
      orderFilters.createdAtFilter[1]?.format('YYYY-MM-DD HH:mm:ss'),
    );
  }
  if (orderFilters.orderTypeFilter) {
    orderFilters.orderTypeFilter.forEach((orderType) => {
      params.append('filter_types[]', orderType);
    });
  }
  if (orderFilters.carrierFilter) {
    orderFilters.carrierFilter.forEach((carrier) => {
      params.append('filter_carriers[]', carrier);
    });
  }
  if (orderFilters.printedFilter !== undefined) {
    params.append('filter_label_printed', orderFilters.printedFilter ? '1' : '0');
  }

  // if (orderFilters.searchValue?.search && orderFilters.searchValue.searchBy) {
  //   params.append(orderFilters.searchValue.searchBy, orderFilters.searchValue.search);
  // }

  return params;
};
